/**
 * Wellness-check editor (W.2) — the operator starts a new wellness check or
 * advances an existing one along the W.1 lifecycle
 * (planned → in_progress → complete | gap_identified). Tracks warming-centre
 * count + vulnerable-resident contact/unreachable tallies (ICS-209 block 34).
 *
 * CREATE → POST /api/communities/:id/wellness-checks (`start_wellness_check`).
 * UPDATE → PATCH /api/wellness-checks/:id (`update_wellness_check` /
 *          `complete_wellness_check`); the status select is restricted to the
 *          transitions the server allows, so the operator can't pick an illegal
 *          move (and the server still enforces it → 409 surfaces as a toast).
 *
 * Modal pattern mirrors AssistanceRequestForm — Esc/backdrop close, validate
 * before submit, toast on success.
 */
import { useEffect, useState } from 'react';
import type { CommunityWellnessCheck, WellnessCheckStatus } from '@tsr/shared-types';
import { createCommunityWellnessCheck, updateCommunityWellnessCheck } from './api';
import { communityStatusStyle } from './styles/communityStatus';
import { getSessionId } from './lib/operatorActions';
import { showToast } from './lib/toast';

/** Mirrors the server's WELLNESS_TRANSITIONS (routes/communities.ts). */
const TRANSITIONS: Record<WellnessCheckStatus, WellnessCheckStatus[]> = {
  planned: ['in_progress', 'gap_identified'],
  in_progress: ['complete', 'gap_identified'],
  gap_identified: ['in_progress', 'complete'],
  complete: [],
};

/** Sensible initial states for a brand-new check (complete is never an opener). */
const CREATE_STATUSES: WellnessCheckStatus[] = ['planned', 'in_progress', 'gap_identified'];

/** Target statuses selectable when editing: the current status (no-op allowed by
 *  the server) plus its legal transitions. */
function allowedTargets(from: WellnessCheckStatus): WellnessCheckStatus[] {
  return [from, ...TRANSITIONS[from]];
}

interface MetricField {
  key:
    | 'warming_centres_active'
    | 'contact_attempts_made'
    | 'vulnerable_residents_contacted'
    | 'vulnerable_residents_unreachable';
  label: string;
}
const METRIC_FIELDS: MetricField[] = [
  { key: 'warming_centres_active', label: 'Warming centres active' },
  { key: 'contact_attempts_made', label: 'Contact attempts made' },
  { key: 'vulnerable_residents_contacted', label: 'Vulnerable contacted' },
  { key: 'vulnerable_residents_unreachable', label: 'Vulnerable unreachable' },
];

export function WellnessCheckEditorForm({
  community,
  existing,
  incidentId,
  onClose,
  onSubmitted,
}: {
  community: { id: string; name: string };
  /** When set, the form edits this check (PATCH); otherwise it starts a new one. */
  existing?: CommunityWellnessCheck | null;
  incidentId?: string | null;
  onClose: () => void;
  onSubmitted: (check: CommunityWellnessCheck) => void;
}): JSX.Element {
  const isUpdate = !!existing;
  const statusOptions = existing ? allowedTargets(existing.status) : CREATE_STATUSES;
  const [status, setStatus] = useState<WellnessCheckStatus>(existing?.status ?? 'planned');
  const [metrics, setMetrics] = useState<Record<MetricField['key'], string>>({
    warming_centres_active: String(existing?.warming_centres_active ?? 0),
    contact_attempts_made: String(existing?.contact_attempts_made ?? 0),
    vulnerable_residents_contacted: String(existing?.vulnerable_residents_contacted ?? 0),
    vulnerable_residents_unreachable: String(existing?.vulnerable_residents_unreachable ?? 0),
  });
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const parseMetric = (v: string): number | null => {
    if (v.trim() === '') return 0;
    const n = Number(v);
    return Number.isInteger(n) && n >= 0 ? n : null;
  };

  const submit = (): void => {
    if (busy) return;
    const parsed: Record<string, number> = {};
    for (const f of METRIC_FIELDS) {
      const n = parseMetric(metrics[f.key]);
      if (n === null) {
        showToast(`${f.label} must be a non-negative whole number.`, { variant: 'error' });
        return;
      }
      parsed[f.key] = n;
    }
    // Guard the lifecycle client-side too (server is authoritative).
    if (isUpdate && existing && status !== existing.status && !TRANSITIONS[existing.status].includes(status)) {
      showToast(`Cannot move ${existing.status} → ${status}.`, { variant: 'error' });
      return;
    }

    setBusy(true);
    const done = (check: CommunityWellnessCheck): void => {
      showToast(isUpdate ? 'Wellness check updated.' : 'Wellness check started.', {
        variant: 'success',
      });
      onSubmitted(check);
    };
    const fail = (err: unknown): void => {
      const msg =
        (err as Error)?.message === 'invalid_transition'
          ? 'That status change is not allowed.'
          : 'Could not save the wellness check. Please retry.';
      showToast(msg, { variant: 'error' });
    };

    if (isUpdate && existing) {
      void updateCommunityWellnessCheck(existing.id, {
        status,
        warming_centres_active: parsed.warming_centres_active!,
        contact_attempts_made: parsed.contact_attempts_made!,
        vulnerable_residents_contacted: parsed.vulnerable_residents_contacted!,
        vulnerable_residents_unreachable: parsed.vulnerable_residents_unreachable!,
        notes: notes.trim() || null,
        session_id: getSessionId(),
      })
        .then(done)
        .catch(fail)
        .finally(() => setBusy(false));
    } else {
      void createCommunityWellnessCheck(community.id, {
        incident_id: incidentId ?? undefined,
        status,
        warming_centres_active: parsed.warming_centres_active!,
        contact_attempts_made: parsed.contact_attempts_made!,
        vulnerable_residents_contacted: parsed.vulnerable_residents_contacted!,
        vulnerable_residents_unreachable: parsed.vulnerable_residents_unreachable!,
        notes: notes.trim() || undefined,
        session_id: getSessionId(),
      })
        .then(done)
        .catch(fail)
        .finally(() => setBusy(false));
    }
  };

  const inputCls =
    'w-full text-xs bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-slate-100 placeholder-slate-500';
  const accent = communityStatusStyle(status).color;

  return (
    <div
      data-testid="wellness-form-backdrop"
      onClick={onClose}
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-6"
    >
      <div
        data-testid="wellness-form-modal"
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col w-full max-w-md max-h-[88vh] rounded-lg border border-amber-700/60 bg-slate-900 text-slate-100 shadow-2xl"
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h2 className="text-sm font-semibold tracking-tight">
            {isUpdate ? 'Update wellness check' : 'Start wellness check'} —{' '}
            <span className="text-amber-300">{community.name}</span>
          </h2>
          <button
            type="button"
            data-testid="wellness-form-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-100 text-lg leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>Status</span>
            <select
              data-testid="wellness-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as WellnessCheckStatus)}
              className={inputCls}
              style={{ borderColor: accent }}
            >
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {communityStatusStyle(s).label}
                </option>
              ))}
            </select>
            {isUpdate && existing?.status === 'complete' && (
              <span className="text-[10px] text-slate-500">
                This check is complete (terminal) — metrics can still be corrected.
              </span>
            )}
          </label>

          <div className="grid grid-cols-2 gap-2">
            {METRIC_FIELDS.map((f) => (
              <label key={f.key} className="text-[11px] text-slate-400 space-y-1">
                <span>{f.label}</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  data-testid={`wellness-${f.key}`}
                  value={metrics[f.key]}
                  onChange={(e) => setMetrics((p) => ({ ...p, [f.key]: e.target.value }))}
                  className={inputCls}
                />
              </label>
            ))}
          </div>

          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>Notes (optional)</span>
            <textarea
              data-testid="wellness-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Outreach detail, gaps, who's responding."
              className={`${inputCls} resize-none`}
            />
          </label>
        </div>

        <footer className="flex items-center gap-2 px-5 py-3 border-t border-slate-700">
          <button
            type="button"
            data-testid="wellness-submit"
            disabled={busy}
            onClick={submit}
            className="text-xs py-1.5 px-3 rounded bg-amber-600/90 hover:bg-amber-600 border border-amber-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
          >
            {busy ? 'Saving…' : isUpdate ? 'Update check' : 'Start check'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-xs py-1.5 px-3 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300"
          >
            Cancel
          </button>
        </footer>
      </div>
    </div>
  );
}
